import { createApi } from '@reduxjs/toolkit/dist/query/react';

import { HydraApiResponse } from 'app/types/hydraData';
import { INotification } from 'app/types/notifications.interface';
import { z3xBaseQuery } from 'app/utils/api.utils';

interface IGetNotificationsArgs{
  page: number
  itemsPerPage: number
}

interface IGetNotificationsResponse {
  notifications: INotification[],
  total: number
}

interface IGetTotalUnreadNotificationsResponse {
  status: string
  count: number
}

export const notificationApi = createApi({
  reducerPath: 'notificationApi',
  baseQuery: z3xBaseQuery({ type: 'ld+json' }),
  tagTypes: ['HeaderNotifications', 'AllNotifications', 'UnreadCount'],
  endpoints: (builder) => ({
    getNotifications: builder.query<IGetNotificationsResponse, IGetNotificationsArgs>({
      query: (params) => ({
        url: '/notification-to-users',
        params: {
          ...params,
          'order[createdAt]': 'desc'
        }
      }),
      providesTags: (result) =>
        result
          ? [
            ...result.notifications.map(({ id }) => ({ type: 'AllNotifications' as const, id })),
            { type: 'AllNotifications' as const, id: 'LIST' }
          ]
          : [{ type: 'AllNotifications' as const, id: 'LIST' }],
      transformResponse: (response: HydraApiResponse<INotification[]>) => {
        return {
          notifications: response['hydra:member'],
          total: response['hydra:totalItems']
        };
      }
    }),
    getHeaderNotifications: builder.mutation<IGetNotificationsResponse, void>({
      query: () => ({
        url: '/notification-to-users',
        params: {
          page: 1,
          itemsPerPage: 5,
          'order[createdAt]': 'desc'
        },
      }),
      transformResponse: (response: HydraApiResponse<INotification[]>) => {
        return {
          notifications: response['hydra:member'],
          total: response['hydra:totalItems']
        };
      }
    }),
    getTotalUnreadNotifications: builder.mutation<number, void>({
      query: () => ({
        url: '/notification/total'
      }),
      transformResponse: (response: IGetTotalUnreadNotificationsResponse) => {
        return response.count;
      }
    }),
    deleteNotifications: builder.mutation<null, number[]>({
      query: (ids) => ({
        url: '/notification/delete',
        method: 'POST',
        body: { ids }
      }),
      invalidatesTags: ['HeaderNotifications', 'AllNotifications', 'UnreadCount'],
    }),
    readNotifications: builder.mutation<null, number[]>({
      query: (ids) => ({
        url: '/notification/read',
        method: 'POST',
        body: { ids }
      }),
      invalidatesTags: ['HeaderNotifications', 'AllNotifications', 'UnreadCount']
    }),
    readAllNotifications: builder.mutation<null, void>({
      query: () => ({
        url: '/notification/read-all',
        method: 'POST',
        body: {}
      }),
      invalidatesTags: ['HeaderNotifications', 'AllNotifications', 'UnreadCount']
    })
  })
});

export const {
  useGetNotificationsQuery,
  useGetHeaderNotificationsMutation,
  useGetTotalUnreadNotificationsMutation,
  useReadNotificationsMutation,
  useDeleteNotificationsMutation,
  useReadAllNotificationsMutation,
} = notificationApi;